import { DirectionalHint, TooltipHost } from "@fluentui/react";
import React, { useState } from "react";
import {
    ElevatedStack,
    Breadcrumb,
    BreadcrumbItem,
    ensure,
    RenderFunction,
    BucketContentExplorer,
    useDisclosure
} from "sprint-common-web";
import {
    isObjectFile,
    isObjectFolder,
    BucketFile,
    BucketObject
} from "sprint-common-web/src/components/explorer/BucketObject";

import { mapToBucketObject } from "../../common/api/delivery-api/BucketInfoResponse";
import { GetUploadBucketFilesQuery } from "../../common/api/delivery-api/GetUploadBucketFilesQuery";
import { SearchUploadBucketQuery } from "../../common/api/delivery-api/SearchUploadBucketQuery";
import { useDeliveryApi } from "../../common/api/delivery-api/useDeliveryApi";
import { OriginatorsDataProvider } from "../../common/data/lookup-data-providers/OriginatorsDataProvider";
import { PageHeader } from "../../common/ui/layout/header/PageHeader";
import { ProcessBucketFilesDialog } from "../common/ProcessBucketFilesDialog";
import { renderSelectedFilesTooltipContent } from "../common/renderSelectedFilesTooltip";
import { DeliveryInfoForm, DeliveryInfoFormInputs, mapFormInputToRequest } from "../delivery-info/DeliveryInfoForm";

export const DeliveryUploadDashboard: React.VFC = () => {
    const {
        getUploadBucketFiles,
        abortGetUploadBucketFilesRequest,
        createDelivery,
        searchUploadBucket
    } = useDeliveryApi();

    const [selectedFiles, setSelectedFiles] = useState<BucketFile[]>([]);

    const [deliveryInfoFormInputs, setDeliveryInfoFormInputs] = useState<DeliveryInfoFormInputs>();
    const { isOpen: isDeliveryFormOpen, onOpen: openDeliveryForm, onClose: closeDeliveryForm } = useDisclosure();
    const { isOpen: isDeliveryModalOpen, onOpen: openDeliveryModal, onClose: closeDeliveryModal } = useDisclosure();

    const handleFormSubmit = async (inputs: DeliveryInfoFormInputs): Promise<void> => {
        setDeliveryInfoFormInputs(inputs);
        openDeliveryModal();
    };

    const handleObjectsSelection = (selectedObjects: BucketObject[]) => {
        if (selectedObjects.some(isObjectFolder)) {
            throw new Error("Folder should not be selected.");
        }

        setSelectedFiles(selectedObjects.filter(isObjectFile));
        openDeliveryForm();
    };

    const handleObjectSelection = (selectedObject: BucketObject) => {
        handleObjectsSelection([selectedObject]);
    };

    const handleProcessBucketFile = async (file: BucketFile) => {
        ensure(deliveryInfoFormInputs);
        await createDelivery(mapFormInputToRequest(deliveryInfoFormInputs, file.path));
    };

    const handleProcessingComplete = () => {
        closeDeliveryModal();
        closeDeliveryForm();
        setDeliveryInfoFormInputs(undefined);
    };

    const handleSearchUploadBucket = async (query: SearchUploadBucketQuery): Promise<BucketObject[]> => {
        /* if search is performed when `getUploadBucketFiles()` request is still pending, 
            then displayed search results would be overridden when this request completed */
        abortGetUploadBucketFilesRequest();

        const response = await searchUploadBucket(query);

        return (response.content ?? []).map(mapToBucketObject);
    };

    const handleOpenFolder = async (query: GetUploadBucketFilesQuery): Promise<BucketObject[]> => {
        const response = await getUploadBucketFiles(query);

        return (response?.content ?? []).map(mapToBucketObject);
    };

    const items: BreadcrumbItem[] = [
        {
            key: "content-ingestion",
            text: "Content Ingestion"
        },
        {
            key: "delivery-bucket",
            text: "Upload",
            onClick: isDeliveryFormOpen
                ? () => {
                      closeDeliveryForm();
                  }
                : undefined
        }
    ];

    if (isDeliveryFormOpen) {
        items.push({
            key: "selected-files",
            text: selectedFiles.length === 1 ? selectedFiles[0].name : `${selectedFiles.length} files selected`,
            tooltipContent: selectedFiles.length > 1 ? renderSelectedFilesTooltipContent(selectedFiles) : undefined
        });
    }

    return (
        <OriginatorsDataProvider>
            <PageHeader>
                <Breadcrumb items={items} onRenderItem={renderBreadcrumb} />
            </PageHeader>
            {isDeliveryFormOpen ? (
                <ElevatedStack>
                    <DeliveryInfoForm onCancel={closeDeliveryForm} onSubmit={handleFormSubmit} horizontal={true} />
                </ElevatedStack>
            ) : (
                <BucketContentExplorer
                    bucketUniqueId={"Upload"}
                    selection={{
                        selectMany: {
                            text: "Upload files",
                            icon: "BulkUpload",
                            onClick: handleObjectsSelection
                        }
                    }}
                    onFileClick={handleObjectSelection}
                    onFolderClick={handleOpenFolder}
                    onSearch={handleSearchUploadBucket}
                />
            )}
            {isDeliveryModalOpen && (
                <ProcessBucketFilesDialog
                    title="Upload to Sprint"
                    message={
                        "You are about to upload files listed below to Sprint. Files will be removed from bucket and will appear on ingest list after small delay."
                    }
                    selectedFiles={selectedFiles}
                    onProcessFile={handleProcessBucketFile}
                    onProcessingComplete={handleProcessingComplete}
                    autoConfirm={true}
                />
            )}
        </OriginatorsDataProvider>
    );
};

const renderBreadcrumb = (item?: BreadcrumbItem, defaultRender?: RenderFunction<BreadcrumbItem>) => {
    ensure(item);
    ensure(defaultRender);

    return (
        <TooltipHost content={item.tooltipContent} directionalHint={DirectionalHint.rightBottomEdge}>
            {defaultRender({ ...item }, defaultRender)}
        </TooltipHost>
    );
};
